import { MessageCircle, Mail, Phone, Globe, MapPin } from "lucide-react";
import { BRAND, MARKETS } from "@/data/site";
import { useI18n } from "@/context/I18nContext";

export default function Footer() {
  const { t } = useI18n();
  return (
    <footer data-testid="site-footer" className="relative z-10 border-t border-brand-border bg-brand-card">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-brand-gold">
            <span className="h-px w-8 bg-brand-gold/70" />
            {BRAND.tagline}
          </div>
          <h2 className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl tracking-tight text-brand-fg leading-[0.98]">
            {t("foot_ready1")} <span className="text-brand-gold italic">{t("foot_ready2")}</span>
          </h2>
          <a href="#inquiry" data-testid="footer-cta" className="mt-8 inline-flex rounded-full bg-brand-gold px-8 py-3.5 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors">
            {t("cta_quote")}
          </a>
        </div>

        <div className="mt-20 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 border-t border-brand-border pt-12">
          <div>
            <img src="/logo.png" alt="Sunrise Mart" className="h-16 w-16 rounded-xl object-cover bg-black" />
            <div className="mt-4 font-display text-2xl text-brand-fg">{BRAND.name}</div>
            <p className="mt-3 text-sm text-brand-fg/60 leading-relaxed max-w-xs">{t("foot_blurb")}</p>
            <div className="mt-5 flex gap-2 text-sm text-brand-fg/60" data-testid="footer-address">
              <MapPin size={15} className="mt-0.5 shrink-0 text-brand-gold" />
              <address className="not-italic leading-relaxed">
                {BRAND.address.map((line) => (<div key={line}>{line}</div>))}
                <a href={BRAND.mapUrl} target="_blank" rel="noreferrer" data-testid="footer-map-link" className="mt-1 inline-block text-brand-gold hover:underline">{t("foot_map")}</a>
              </address>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-brand-muted">{t("foot_contact")}</div>
            <div className="mt-4 space-y-3 text-sm">
              <a href={`https://wa.me/${BRAND.whatsappRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-fg/80 hover:text-brand-gold transition-colors"><MessageCircle size={14} /> {BRAND.whatsapp}</a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 text-brand-fg/80 hover:text-brand-gold transition-colors"><Mail size={14} /> {BRAND.email}</a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-brand-fg/80 hover:text-brand-gold transition-colors"><Phone size={14} /> {BRAND.phone}</a>
              <div className="flex items-center gap-2 text-brand-fg/80"><Globe size={14} /> sunrise-mart.com</div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-brand-muted">{t("foot_markets")}</div>
            <div className="mt-4 space-y-2 text-sm text-brand-fg/80">
              {MARKETS.map((m) => (<div key={m}>{m}</div>))}
              <div className="text-brand-gold">{t("glob_new")}</div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-brand-muted">{t("foot_explore")}</div>
            <div className="mt-4 space-y-2 text-sm text-brand-fg/80">
              <a href="#products" className="block hover:text-brand-gold transition-colors">{t("nav_products")}</a>
              <a href="#process" className="block hover:text-brand-gold transition-colors">{t("nav_process")}</a>
              <a href="#certifications" className="block hover:text-brand-gold transition-colors">{t("nav_certs")}</a>
              <a href="#inquiry" className="block hover:text-brand-gold transition-colors">{t("nav_contact")}</a>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-brand-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-brand-fg/40">
          <span>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</span>
          <span>Local to Global.</span>
        </div>
      </div>
    </footer>
  );
}
